import { ChangeDetectionStrategy, Component, computed, inject, input, signal, ViewEncapsulation } from "@angular/core";
import { AuthService, DateSelection, UserSelectionService } from "@front/m19-services";
import { DatePipe, DecimalPipe, formatPercent } from "@angular/common";
import { otherBrandColor, SovBrandDateType } from "../../share-of-voice/share-of-voice.component";
import { TranslocoDirective } from "@jsverse/transloco";
import { SpinnerComponent } from "@front/m19-ui";
import { ChartConfig, IChartComponent, IIconComponent, ISwitchButtonComponent, ITooltipDirective } from "@front/m19-ui";
import { GroupByFn } from "@front/m19-models/dataset/Dataset";
import { Metric, MetricCategory, PALETTE, SummableMetric } from "@front/m19-metrics";
import { toSignal } from "@angular/core/rxjs-interop";

enum PieChartDataMode {
  GLOBAL = "GLOBAL",
  SNAPSHOT = "SNAPSHOT",
}

interface SovDataByBrand {
  brand: string;
  value: number;
}

class SovMetric extends SummableMetric<SovDataByBrand> {
  format(d: number | SovDataByBrand, locale: string, currency: string, precision?: string): string {
    const value = typeof d === "number" ? d : d.value;
    return formatPercent(value / 100, locale, precision);
  }
}

export const SOV: Metric<SovDataByBrand> = new SovMetric({
  id: "SOV",
  field: "value",
  title: "Share of voice",
  category: MetricCategory.OTHER,
  color: PALETTE[10],
});

@Component({
  selector: "app-sov-pie-chart",
  templateUrl: "./sov-pie-chart.component.html",
  encapsulation: ViewEncapsulation.None,
  imports: [
    TranslocoDirective,
    ITooltipDirective,
    DecimalPipe,
    SpinnerComponent,
    ISwitchButtonComponent,
    IIconComponent,
    IChartComponent,
    DatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SovPieChartComponent {
  private authService = inject(AuthService);
  private userSelectionService = inject(UserSelectionService);

  protected readonly PieChartDataMode = PieChartDataMode;
  protected readonly otherBrandColor = otherBrandColor;

  hoveredBrand = input<string>();
  brandSelection = input<string[]>([]);
  sovData = input<SovBrandDateType>(new Map());
  snapshotDate = input<Date | undefined>(undefined);
  loading = input<boolean>();
  brandColors = input<Map<string, string>>();

  selectedDateRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });
  locale = toSignal(this.authService.userLocale$, { requireSync: true });

  dataMode = signal<PieChartDataMode>(PieChartDataMode.SNAPSHOT);

  readonly donutChartConfig = computed<ChartConfig<SovDataByBrand, "doughnut">>(() => {
    const groupBy: GroupByFn<SovDataByBrand> = (d) => {
      return {
        key: d.brand,
        label: d.brand,
        color: this.brandColors()?.get(d.brand) ?? otherBrandColor,
      };
    };

    return {
      aspectRatio: 1,
      type: "doughnut",
      groupByFunctions: [groupBy],
      aggFunctions: [],
      metrics: [SOV],
      hideLegend: true,
      hideDatalabels: true,
    };
  });

  data = computed<SovDataByBrand[]>(() => {
    const dataByBrand: SovDataByBrand[] = [];
    const sovData = this.sovData();
    const snapshotDate = this.snapshotDate();
    const selectedBrands = this.brandSelection();

    let sovSum = 0;
    for (const brand of selectedBrands) {
      let brandData = 0;
      if (this.dataMode() === PieChartDataMode.GLOBAL) {
        brandData = this.getAggregatedDataForBrand(sovData, brand, this.selectedDateRange())!;
      } else if (snapshotDate) {
        brandData = sovData.get(brand)?.get(snapshotDate.getTime()) ?? 0;
      }
      sovSum += brandData;
      dataByBrand.push({ brand, value: brandData });
    }

    dataByBrand.push({ brand: "Other brands", value: 100 - sovSum });

    return dataByBrand;
  });

  changeDataMode(mode: PieChartDataMode) {
    this.dataMode.set(mode);
  }

  // In 'GLOBAL' mode, computes average brand score on selected date range;
  private getAggregatedDataForBrand(
    sovByBrandDate: SovBrandDateType,
    brand: string,
    dateRange: DateSelection,
  ): number | undefined {
    let averageScoreOnPeriod = 0;
    let scoresNb = 0;

    const scores: Map<number, number> | undefined = sovByBrandDate.get(brand);
    if (!scores) return undefined;
    for (const [k, v] of scores) {
      if (k < dateRange.start.toMillis() || k > dateRange.end.toMillis()) continue;
      averageScoreOnPeriod += v;
      scoresNb += 1;
    }
    return scoresNb > 0 ? averageScoreOnPeriod / scoresNb : 0;
  }
}
